import type { MailingAddress, Metafield, MoneyV2, UserError } from "../types/storefront.types";

export interface IReturnLineItem {
  fulfillmentLineItem: {
    quantity: number;
    discountedTotalSet: { shopMoney: MoneyV2 };
    originalTotalSet: { shopMoney: MoneyV2 };
    lineItem: {
      name: string;
      vendor: string;
      id: string;
      image: { url: string; height: number; width: number };
      product: { id: string; thumbnailImages?: Metafield };
      title: string;
      variantTitle: string;
      variant: { selectedOptions: { name: string; value: string }[] };
      discountAllocations: { allocatedAmount: MoneyV2 }[];
    };
  };
  customerNote: string;
  id: string;
  quantity: number;
  refundedQuantity: number;
  returnReason: string;
}

export interface IReturn {
  suggestedRefund: {
    amount: { shopMoney: MoneyV2 };
    shipping: { amountSet: { shopMoney: MoneyV2 } };
    subtotal: { shopMoney: MoneyV2 };
    discountedSubtotal: { shopMoney: MoneyV2 };
    totalTax: { shopMoney: MoneyV2 };
    totalDuties: { shopMoney: MoneyV2 };
  };
  status: string;
  name: string;
  tags: string[];
  customAttributes: { value: string; key: string }[];
  order: {
    id: string;
    name: string;
    customer: { id: string };
    shippingAddress: MailingAddress;
    billingAddress: MailingAddress;
    customAttributes?: { key: string; value: string }[];
    tags?: string[];
  };
  totalQuantity: number;
  returnLineItems: { edges: { node: IReturnLineItem }[] };
}

export interface IFetchReturnableOrder {
  order: IOrder;
  returnableFulfillmentLineItems: IReturnableFulfillmentLineItem[];
}

export interface IOrder {
  createdAt: string;
  name: string;
  country: string;
  returnStatus: string;
  totalOrderItemCount: number;
  totalReturnedItemCount: number;
}

export interface IReturnableFulfillmentLineItem {
  fulfillmentLineItem: {
    id: string;
    quantity: number;
    weight?: { unit: string; value: number };
    originalTotalSet: { shopMoney: { amount: string } };
    discountedTotalSet: { shopMoney: { amount: string } };
    lineItem: ILineItem;
  };
  quantity: number;
}

export interface ILineItem {
  currentQuantity: number;
  nonFulfillableQuantity: number;
  originalTotalSet: { shopMoney: { amount: string } };
  discountedTotalSet: { shopMoney: { amount: string } };
  discountAllocations: { allocatedAmount: { amount: string } }[];
  id: string;
  name: string;
  title: string;
  image: { width: number; url: string; height: number } | null;
  product: {
    handle: string;
    id: string;
    status: string;
    tags: string[];
    title: string;
    vendor: string;
    thumbnailImages?: Metafield;
  };
  quantity: number;
  variant: {
    availableForSale: boolean;
    compareAtPrice: string;
    displayName: string;
    id: string;
    image: { width: number; url: string; height: number } | null;
    price: string;
    selectedOptions: { name: string; value: string }[];
  } | null;
  variantTitle: string;
  vendor: string;
}

export interface IRequestReturnParams {
  shop: string;
  domain?: "kr" | "intl" | "";
  orderId: string;
  returnLineItems: { fulfillmentLineItemId: string; quantity: number; returnReason: TReturnReason | string; customerNote: string }[];
  returnImageUrls: string[];
  returnShippingFee: number;
}

export interface IRefundDuty {
  dutyId: string;
  refundType: "FULL" | "PROPORTIONAL";
}

export interface IRequestReturnResponse {
  returnId: string;
  returnRefundLineItems: { returnLineItemId: string; quantity: number }[];
  refundDuties: IRefundDuty[];
  userErrors?: UserError[];
}

export type TReturnReason =
  | typeof ReturnReason.DEFAULT
  | typeof ReturnReason.DEFECTIVE
  | typeof ReturnReason.UNWANTED
  | typeof ReturnReason.WRONG_ITEM
  | typeof ReturnReason.SIZE_TOO_LARGE
  | typeof ReturnReason.SIZE_TOO_SMALL
  | typeof ReturnReason.OTHER;

export enum ReturnReason {
  DEFAULT = "DEFAULT",
  DEFECTIVE = "DEFECTIVE",
  UNWANTED = "UNWANTED",
  WRONG_ITEM = "WRONG_ITEM",
  SIZE_TOO_LARGE = "SIZE_TOO_LARGE",
  SIZE_TOO_SMALL = "SIZE_TOO_SMALL",
  OTHER = "OTHER",
}

export interface IFetchReturnOrder {
  data: { return: IReturn };
}

export interface IReturnRequestedItem {
  id: string;
  quantity: number;
  isCheck: boolean;
  image: File[] | null;
  returnReason: TReturnReason;
  customerNote: string;
  weight?: { value: number; unit: string };
}
